import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';

/**
 * Pantalla de carga de arranque y de los tramos con `React.lazy`.
 *
 * ANTES era un `<Backdrop>` de MUI, y eso causaba dos problemas en móvil:
 *
 * 1. El Backdrop es un VELO translúcido (`rgba(0,0,0,0.5)`) sobre la app. En un tema off-black
 *    no se lee como "cargando" sino como si la pantalla se hubiera ensuciado, y encima captura
 *    los toques: mientras estuviera puesto, nada respondía. Ahora la superficie es OPACA y usa
 *    el mismo fondo de la app, así que se ve como una pantalla de carga de verdad.
 *
 * 2. Aparecía SIEMPRE, incluso en transiciones de 80 ms, y ese parpadeo negro entre pantalla y
 *    pantalla era justo lo que se sentía como "se traba al pasar de un lado a otro". Con el
 *    retardo de abajo, una navegación rápida no pinta absolutamente nada.
 */

// Por debajo de esto la carga se percibe como instantánea y mostrar algo solo produce un
// parpadeo. Es el umbral clásico de "respuesta inmediata" en interfaces.
const DELAY_MS = 250;

// Colores LITERALES de respaldo. Este componente se monta en DOS sitios y uno de ellos
// (`index.js`, el `PersistGate`) está FUERA del ThemeProvider: allí `surface.app` y `gold.main`
// no existen, el `background-color` resultaba inválido y quedaba una capa fija a pantalla
// completa TRANSPARENTE que igualmente se comía todos los toques mientras rehidrataba el estado.
// Un bloqueador invisible. Con el respaldo, la capa se pinta siempre y se ve lo que debe verse.
const FALLBACK_BG = '#0E1012';
const FALLBACK_ACCENT = '#F9BD4E';

function FullScreenLoading() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), DELAY_MS);

    // Si el contenido llegó antes del retardo, el componente se desmonta y el temporizador se
    // cancela: nunca llega a pintarse nada.
    return () => clearTimeout(timer);
  }, []);

  if (!visible) return null;

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-busy="true"
      sx={{
        position: 'fixed',
        inset: 0,
        // Opaco a propósito (ver punto 1): un velo translúcido sobre off-black parece suciedad.
        // Con la forma de función se resuelve el token cuando HAY tema y se cae al literal cuando
        // no lo hay, en vez de generar CSS inválido.
        backgroundColor: (theme) => theme.palette?.surface?.app || FALLBACK_BG,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Por encima del drawer pero POR DEBAJO de los modales (1300): un diálogo abierto que
        // dispare una carga no puede quedar tapado por su propio loader.
        zIndex: (theme) => theme.zIndex.drawer + 1
      }}>
      <CircularProgress
        size={34}
        thickness={5}
        sx={{ color: (theme) => theme.palette?.gold?.main || FALLBACK_ACCENT }}
      />
    </Box>
  );
}

export default FullScreenLoading;
